import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { cn } from '../lib/utils';

const routeLabels = {
  '/': 'Home',
  '/product-research': 'Product Research',
  '/winning-products': 'Winning Products',
  '/ads': 'Creative Ads',
  '/shopify': 'Shopify',
  '/product': 'Product Details',
};

export function Breadcrumbs({ productName }) {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // On home page, just show "Home"
  if (pathSegments.length === 0) {
    return (
      <nav className="flex items-center gap-2 text-sm" data-testid="breadcrumbs">
        <div className="flex items-center gap-2 text-slate-600 font-medium">
          <Home className="w-4 h-4" />
          <span>Home</span>
        </div>
      </nav>
    );
  }

  const breadcrumbs = [];
  let currentPath = '';

  // Always add Home as first breadcrumb
  breadcrumbs.push({
    path: '/',
    label: 'Home',
    isLast: false,
  });

  // Build breadcrumbs from path segments
  pathSegments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const isLast = index === pathSegments.length - 1;
    
    // Handle product detail pages (e.g., /product/:ecomId)
    if (segment === 'product' && pathSegments[index + 1]) {
      breadcrumbs.push({
        path: currentPath,
        label: 'Products',
        isLast: false,
      });
    } else if (pathSegments[index - 1] === 'product') {
      breadcrumbs.push({
        path: currentPath,
        label: productName || 'Product Details',
        isLast: true,
      });
    } 
    // Handle winning-products/:ecomId
    else if (segment === 'winning-products' && pathSegments[index + 1]) {
      breadcrumbs.push({
        path: '/winning-products',
        label: 'Winning Products',
        isLast: false,
      });
    } else if (pathSegments[index - 1] === 'winning-products') {
      breadcrumbs.push({
        path: currentPath,
        label: productName || 'Product Details',
        isLast: true,
      });
    }
    // Handle ads/ugc/:ecomId and ads/static/:ecomId
    else if (segment === 'ads' && pathSegments[index + 1]) {
      breadcrumbs.push({
        path: '/ads',
        label: 'Creative Ads',
        isLast: false,
      });
    } else if (pathSegments[index - 1] === 'ads' && (segment === 'ugc' || segment === 'static')) {
      // Skip ugc/static intermediate paths
    } else if ((pathSegments[index - 2] === 'ads') && (pathSegments[index - 1] === 'ugc' || pathSegments[index - 1] === 'static')) {
      const adType = pathSegments[index - 1] === 'ugc' ? 'UGC Ad' : 'Static Ad';
      breadcrumbs.push({
        path: currentPath,
        label: productName || adType,
        isLast: true,
      });
    }
    // Handle shopify/:ecomId
    else if (segment === 'shopify' && pathSegments[index + 1]) {
      breadcrumbs.push({
        path: '/shopify',
        label: 'Shopify',
        isLast: false,
      });
    } else if (pathSegments[index - 1] === 'shopify') {
      breadcrumbs.push({
        path: currentPath,
        label: productName || 'Product Details',
        isLast: true,
      });
    }
    // Default case
    else {
      breadcrumbs.push({
        path: currentPath,
        label: routeLabels[currentPath] || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '),
        isLast,
      });
    }
  });

  return (
    <nav className="flex items-center gap-2 text-sm" data-testid="breadcrumbs">
      {breadcrumbs.map((crumb, index) => (
        <div key={`${crumb.path}-${index}`} className="flex items-center gap-2">
          {index > 0 && (
            <ChevronRight className="w-4 h-4 text-slate-400" />
          )}
          {crumb.isLast ? (
            <span className="text-slate-600 font-medium truncate max-w-[200px]">
              {crumb.label}
            </span>
          ) : (
            <Link
              to={crumb.path}
              className={cn(
                'text-slate-500 hover:text-cyan-600 transition-colors',
                'flex items-center gap-1'
              )}
            >
              {index === 0 && <Home className="w-4 h-4" />}
              <span>{crumb.label}</span>
            </Link>
          )}
        </div>
      ))}
    </nav>
  );
}
